import classNames from 'classnames';
import { PropsWithChildren, forwardRef } from 'react';

import { Icon } from '@/components/shared/Icon';
import type { IconName } from '@/types/shared';

export type BadgeProps = PropsWithChildren<{
  variant:
  | 'success'
  | 'danger'
  | 'primary'
  | 'beige'
  | 'purple'
  | 'neutral'
  | 'orange'
  | 'info'
  | 'olive'
  | 'yellow';
  sizeVariant?: 'small' | 'base' | 'large';
  iconName?: IconName;
  shapeVariant?: 'rectangle' | 'rounded';
  isOutlined?: boolean;
  className?: string;
}>;

// Match the legacy compound selectors so caller classes retain their existing precedence.
const appearanceClasses: Record<BadgeProps['variant'], string> = {
  success: 'tw:[&&]:text-success tw:[&&]:bg-green-100',
  danger: 'tw:[&&]:text-danger tw:[&&]:bg-red-100',
  primary: 'tw:[&&]:text-primary-500 tw:[&&]:bg-primary-200',
  beige: 'tw:[&&]:text-beige-600 tw:[&&]:bg-beige-200',
  purple: 'tw:[&&]:text-purple-500 tw:[&&]:bg-purple-100',
  neutral: 'tw:[&&]:text-neutral-700 tw:[&&]:bg-neutral-100',
  orange: 'tw:[&&]:text-orange-500 tw:[&&]:bg-orange-100',
  info: 'tw:[&&]:text-info tw:[&&]:bg-info-surface',
  olive: 'tw:[&&]:text-success tw:[&&]:bg-green-200/50',
  yellow: 'tw:[&&]:text-yellow-600 tw:[&&]:bg-yellow-200',
};

const outlineClasses: Record<BadgeProps['variant'], string> = {
  success: 'tw:[&&&]:outline-success',
  danger: 'tw:[&&&]:outline-danger',
  primary: 'tw:[&&&]:outline-primary-500',
  beige: 'tw:[&&&]:outline-beige-600',
  purple: 'tw:[&&&]:outline-purple-500',
  neutral: 'tw:[&&&]:outline-neutral-700',
  orange: 'tw:[&&&]:outline-orange-500',
  info: 'tw:[&&&]:outline-info',
  olive: 'tw:[&&&]:outline-success',
  yellow: 'tw:[&&&]:outline-yellow-600',
};

const sizeClasses = {
  small: 'tw:[&&]:px-1 tw:[&&]:min-w-6 tw:[&&]:h-6 tw:[&&]:text-body-small',
  base: 'tw:[&&]:px-2 tw:[&&]:h-8 tw:[&&]:text-body-small',
  large: 'tw:[&&]:px-3 tw:[&&]:py-4 tw:[&&]:leading-[var(--gs-font-body-md)] tw:[&&]:text-body-base',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({
  children,
  variant,
  sizeVariant = 'base',
  iconName,
  shapeVariant = 'rounded',
  isOutlined,
  className,
}, ref) => {
  // Legacy info rectangles have no outline; orange outlines always use the base radius.
  const hasOutline = isOutlined && (variant !== 'info' || shapeVariant === 'rounded');
  const hasRoundedOutline = hasOutline && shapeVariant === 'rounded' && variant !== 'orange';

  const badgeClassName = classNames(
    'tw:rounded-base tw:min-w-8 tw:inline-flex tw:items-center tw:justify-center',
    'tw:whitespace-nowrap tw:leading-[100%] tw:tracking-body',
    appearanceClasses[variant],
    sizeClasses[sizeVariant],
    shapeVariant === 'rounded' ? 'tw:[&&]:rounded-full' : 'tw:[&&]:rounded-base',
    hasOutline && [
      'tw:[&&&]:outline-1 tw:[&&&]:outline-solid',
      outlineClasses[variant],
      hasRoundedOutline ? 'tw:[&&&]:rounded-full!' : 'tw:[&&&]:rounded-base',
    ],
    className,
  );

  const iconClassName = classNames('tw:w-4 tw:h-4', children && 'tw:[&&]:mr-1');

  return (
    <span className={badgeClassName} ref={ref}>
      {
        iconName &&
        <Icon
          name={iconName}
          className={iconClassName}
        />
      }
      {children}
    </span>
  );
});

Badge.displayName = 'Badge';
